import { t } from 'react-switch-lang';
import { Card, CardBody, Col, Row } from 'react-bootstrap';
import { useRouter } from 'next/router';
import { Form } from 'react-final-form';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import SectionTitle from '../../SectionTitle';
import Button from '../../Button';
import styles from '../../../styles/ReviewStep.module.scss';
import { events, logAmpEvent } from '../../../utils/Amplitude';
import { DISPATCHERS, setReferralUrl, usePaymentProcessContext } from '../../../context/PaymentProcessContext';
import PaymentInformation from '../PaymentInformation';
import ReplaceTextWithElement from '../../ReplaceTextWithElement';
import { TextField } from '../../InputFields';
import { executeReCaptcha, invalidFormAlert } from '../../../utils/Helpers';
import { beginPayment, apiErrorHandled } from '../../../utils/ApiClient';
import ErrorAlert from '../../ErrorAlert';
import PayPal, { payPalRedirectError } from '../PayPal';
import ConfirmationModal from '../../ConfirmationModal';
import GoogleRecaptchaDisclaimer from '../../GoogleRecaptchaDisclaimer';
import { toErrorPage } from '../../ErrorDisplay';
import { isProduction } from '../../../utils/HostingEnv';

const additionalPaymentOptionInfoStrings = {
  Credit: 'ReviewPage_CreditCard_Instructions',
  InPerson: 'ReviewInstructionsInPerson',
  PayPal: 'ReviewPage_PayPal_Instructions',
  eTransfer: 'ReviewInstructionseTransfer',
  PeoplesRFM: 'ReviewInstructionsPeoplesRFM',
};

export default function Review() {
  const {
    dispatchPaymentProcess,
    paymentProcess: {
      paymentOption,
      partner,
      accountDetails,
      referral,
      siteMode,
    },
  } = usePaymentProcessContext();
  const router = useRouter();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const additionalPaymentOptionInfo = additionalPaymentOptionInfoStrings[paymentOption?.Name];
  const isPOCreditCard = paymentOption?.Name === 'Credit';
  const isPOPayPal = paymentOption?.Name === 'PayPal';
  const isPOPeoplesRFM = paymentOption?.Name === 'PeoplesRFM';
  const recaptchaRef = useRef(null);

  const onSubmit = async (values) => {
    setLoading(true);
    setError('');

    let Referral = referral;
    if (document.referrer) { // takes precendence over referral from prepopulate
      Referral = setReferralUrl(referral, document.referrer);
    }

    const recaptchaToken = await executeReCaptcha(recaptchaRef.current, 'CANCEL', () => setLoading(false)).catch(() => '');
    if (!recaptchaToken) return undefined;
    const auth = `reCAPTCHA ${recaptchaToken}`;

    recaptchaRef.current.reset();
    const resp = await beginPayment(auth, {
      PartnerSeq: partner.PartnerSeq,
      PaymentOption: paymentOption.Name,
      Fee: paymentOption.totalFee.toFixed(2),
      Fields: accountDetails,
      Language: router.locale,
      PostalCode: isPOCreditCard ? values.postalCode.trim() : '',
      ReturnUrl: isPOPeoplesRFM ? `${window.location.origin}/${router.locale}/complete-payment` : '',
      Referral,
      SiteMode: siteMode === 'Intuit' ? siteMode : '',
    });
    if (resp.Result === 11 || resp.Result === 12) {
      const errorPage = toErrorPage(resp.Description, isProduction ? '' : `Debug: ${resp.ErrorDebug}`);
      if (isPOPayPal) throw payPalRedirectError(errorPage);
      router.push(errorPage);
      return undefined;
    }
    let errMsgCallback = setError;
    if (isPOPayPal) errMsgCallback = (msg) => { throw payPalRedirectError(toErrorPage(msg)); };
    if (apiErrorHandled(resp, errMsgCallback, router)) return undefined;
    const {
      Result,
      Description,
      PaymentAmount,
      Fee,
      TotalAmount,
      SessionToken,
      ...paymentCode
    } = resp;
    dispatchPaymentProcess(
      DISPATCHERS.BEGIN_PAYMENT(paymentCode, Fee, PaymentAmount, TotalAmount, SessionToken)
    );

    // stay on current page for PayPal, PayPal button opens popup
    if (values === 'PAYPAL') return paymentCode.OrderId;

    if (isPOPeoplesRFM) { // redirect to hosted payment page for PeoplesRFM
      window.location.href = paymentCode.PaymentUrl;
    } else { // otherwise navigate to complete payment page
      router.push('/complete-payment');
    }
    return undefined;
  };

  const validate = (values) => {
    const errors = {};
    if (!values.postalCode || values.postalCode?.trim().length < 1) {
      errors.postalCode = t('Validation_Required');
    }
    return errors;
  };

  const onRecaptchaError = () => {
    logAmpEvent(events.GOOGLE_RECAPTCHA_ERROR);
    setError(t('GoogleRecaptchaFailureMsg'));
    setLoading(false);
  };

  const handleCancel = () => {
    dispatchPaymentProcess(DISPATCHERS.START_OVER());
    router.push('/');
  };

  useEffect(() => {
    logAmpEvent(events.USER_VIEWED_REVIEW_PAGE);
  }, []);

  let submit;
  return (
    <>
      <Card>
        <CardBody>
          <Row className="justify-content-center">
            <ErrorAlert msg={error} />
          </Row>
          <Row>
            <Col md={12}>
              <SectionTitle
                title={t('ReviewPage_Heading_Title')}
              />
            </Col>
          </Row>
          <Row className="justify-content-center mb-4">
            <Col xl={6} lg={8} md={10} xs={12}>
              <PaymentInformation hideSecureText showEditButtons />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <GoogleRecaptchaDisclaimer />
            </Col>
          </Row>
          {additionalPaymentOptionInfo && (
          <Row>
            <Col md={12} className={styles.additionalPaymentOptionInfo}>
              <ReplaceTextWithElement
                text={t(additionalPaymentOptionInfo)}
                replaceWith={<i>Interac</i>}
                replaceKey="Interac"
              />
            </Col>
          </Row>
          )}
        </CardBody>
        <div className={styles.billerDisclaimer} role="alert">
          {t('Review_SelectCorrectBillerDisclaimer')}
        </div>
      </Card>
      <ReCAPTCHA
        style={{ display: 'none' }}
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
        onErrored={onRecaptchaError}
      />
      {isPOCreditCard && (
        <Form
          onSubmit={onSubmit}
          validate={(values) => validate(values)}
          render={({
            handleSubmit,
            invalid,
            errors,
          }) => {
            submit = async (event) => {
              invalidFormAlert(invalid, errors);
              await handleSubmit(event);
            };
            return (
              <Row className={styles.postalCodeFieldContainer}>
                <Col md={{ span: 6, offset: 6 }} xs={12}>
                  <form onSubmit={submit}>
                    <TextField
                      label={t('ReviewPage_PostalCodeField_Label')}
                      formatText={t('ReviewPage_PostalCodeField_HelpText')}
                      id="postalCode"
                      name="postalCode"
                      autoComplete="postal-code"
                    />
                  </form>
                </Col>
              </Row>
            );
          }}
        />
      )}
      <ConfirmationModal
        showModal={showConfirmationModal}
        onCancel={() => setShowConfirmationModal(false)}
        onConfirm={handleCancel}
        ampEventPropsPageName="Payment Review"
        stepName={t('ProgressBarStep4')}
        returnFocusTo="cancelButton"
      />
      <div className={styles.buttonContainer}>
        <Button
          text={t('ReviewPage_Cancel_Button')}
          ampEvent={events.USER_CLICKED_CANCEL_BUTTON}
          ampEventProps={{ Step: 'Payment Review' }}
          onClick={() => setShowConfirmationModal(true)}
          id="cancelButton"
        />
        {isPOPayPal ? (
          <PayPal
            createOrder={() => onSubmit('PAYPAL')}
            ampEvent={events.USER_CLICKED_CONTINUE_BUTTON}
            ampEventProps={{ Step: 'Payment Review' }}
            reCaptchaRef={recaptchaRef}
          />
        ) : (
          <Button
            loading={loading}
            text={t('PaymentProcess_ButtonContinue')}
            ampEvent={events.USER_CLICKED_CONTINUE_BUTTON}
            ampEventProps={{ Step: 'Payment Review' }}
            onClick={async (event) => {
              if (submit) {
                await submit(event);
              } else {
                await onSubmit();
              }
            }}
          />
        )}
      </div>
    </>
  );
}
